import {reaction, observable, computed, action} from 'mobx';
import {nonenumerable} from 'core-decorators';
import objectAssignDeep from 'utils/object-assign-deep';

import APIRequestState from '../_util/api-request';
import ModelState from '../_util/model';

class LoadUserAddressState extends ModelState {
  sanitizeModelId(id) { return parseFloat(id); }

  get url() { return '/me/addresses'; }
  get singular() { return 'address'; }
  get plural() { return 'addresses'; }
}

class LoadUserAddressesState extends APIRequestState {
  get method() { return 'GET'; }
  get url() { return '/me/addresses'; }
}

class AddUserAddressesState extends APIRequestState {
  get method() { return 'POST'; }
  get url() { return '/me/addresses'; }
}

class UpdateUserAddressState extends APIRequestState {
  get method() { return 'PUT'; }
  get url() { return '/me/addresses'; }
}

class DeleteUserAddressState extends APIRequestState {
  get method() { return 'DELETE'; }
  get url() { return '/me/addresses'; }
}

class UserAddressesState {
  @observable _load;
  @observable _add;
  @observable _loadOne;
  @observable _update;
  @observable _delete;

  @computed get isLoading() { return this._load && this._load.isLoading || false; }
  @computed get isLoaded() { return this._load && !!this._load.response || false; }
  @computed get isLoadingOne() { return this._loadOne && this._loadOne.isLoading || false; }
  @computed get isLoadedOne() { return this._loadOne && !!this._loadOne.response || false; }
  @computed get error() { return this._load && this._load.error || null; }

  maybeLoadOne(...args) {
    if (!this._loadOne) this._loadOne = new LoadUserAddressState(this.ctx);
    return this._loadOne.maybeLoadOne(...args);
  }

  maybeLoad(...args) {
    if (!this._load) this._load = new LoadUserAddressesState(this.ctx);
    return this._load.maybeLoad(...args);
  }

  add(...args) {
    if (!this._add) this._add = new AddUserAddressesState(this.ctx);
    return this._add.load(...args);
  }

  update(...args) {
    if (!this._update) this._update = new UpdateUserAddressState(this.ctx);
    if (this._load) this._load.reset();
    return this._update.put(...args);
  }

  delete(...args) {
    if (!this._delete) this._delete = new DeleteUserAddressState(this.ctx);
    return this._delete.put(...args);
  }

  @observable allAddresses = [];

  reset() {
    if (this._load) this._load.reset();
  }

  resetOne() {
    this._loadOne = undefined;
  }

  abort() {
    if (this._add) {
      this._add.abort();
    }
    if (this._load) {
      this._load.abort();
    }
    if (this._loadOne) {
      this._loadOne.abort();
    }
    if (this._update) {
      this._update.abort();
    }
    if (this._delete) {
      this._delete.abort();
    }
  }

  @nonenumerable
  listeners = [];

  constructor(ctx) {
    Object.defineProperty(this, 'ctx', {
      value: ctx,
      enumerable: false,
    });

    this.listeners.push(reaction(
      () => this._load && this._load.response && this._load.response.addresses,
      (addresses) => {
        this.allAddresses = Array.from(addresses || []);
      },
      {
        name: 'Add addresses to list when they\'re loaded',
        fireImmediately: true,
        compareStructural: true,
      },
    ));
  }

  @action deserialize(value) {
    const {
      _load, _add, _loadOne, _update, _delete, ...rest
    } = value;
    objectAssignDeep(this, rest);
    if (_load) {
      this._load = new LoadUserAddressesState(this.ctx);
      objectAssignDeep(this._load, _load);
    }
    if (_add) {
      this._add = new AddUserAddressesState(this.ctx);
      objectAssignDeep(this._add, _add);
    }
    if (_loadOne) {
      this._loadOne = new LoadUserAddressState(this.ctx);
      objectAssignDeep(this._loadOne, _loadOne);
    }
    if (_update) {
      this._update = new UpdateUserAddressState(this.ctx);
      objectAssignDeep(this._update, _update);
    }
    if (_delete) {
      this._delete = new DeleteUserAddressState(this.ctx);
      objectAssignDeep(this._delete, _delete);
    }
  }

  dispose() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }
}

export default UserAddressesState;



// WEBPACK FOOTER //
// ./spa/state/user/user-addresses.jsx